export interface Level {
  id: number;
  name: string;
  level_number: number;
}

export interface GeneratedLesson {
  id: number;
  uuid: string;
  status: string;
  updated_at: string;
  structured_content?: unknown | null;
  quiz_generated: boolean;
  quiz_generation_attempted: boolean;
  quiz_generation_error: string | null;
}

export interface Lesson {
  id: number;
  title: string;
  lesson_type?: string;
  generated_lessons: GeneratedLesson[] | GeneratedLesson | null;
  _count: {
    content_chunks: number;
  };
}

export interface Module {
  id: number;
  title: string;
  lessons: Lesson[];
  levels: Level | null;
}

export interface PlacementQuiz {
  id: number;
  status: string;
  updated_at: string;
  title: string | null;
  level_name: string | null;
  quiz_data?: unknown;
  _count: {
    placement_questions: number;
  };
}

export function isStaticPlacementQuizData(quizData: unknown): boolean {
  if (!quizData || typeof quizData !== "object") return false;
  return (quizData as { source?: string }).source === "static";
}

function getCanonicalGeneratedLesson(
  raw: Lesson["generated_lessons"],
): GeneratedLesson | null {
  if (!raw) return null;
  const gls = Array.isArray(raw) ? raw : [raw];
  return gls.find((gl) => gl) ?? null;
}

/** True when every lesson has authored block-format canonical content. */
export function isBlockOnlyCourse(course: Pick<CourseDetails, "modules">): boolean {
  const lessons = course.modules.flatMap((m) => m.lessons);
  if (lessons.length === 0) return false;
  return lessons.every((lesson) => {
    const gl = getCanonicalGeneratedLesson(lesson.generated_lessons);
    if (!gl?.structured_content) return false;
    return (
      typeof gl.structured_content === "object" &&
      gl.structured_content !== null &&
      (gl.structured_content as { format?: string }).format === "blocks"
    );
  });
}

export function courseHasStaticPlacement(
  placementQuizzes: PlacementQuiz[] | undefined,
): boolean {
  return (placementQuizzes ?? []).some((q) => isStaticPlacementQuizData(q.quiz_data));
}

export function getPlacementQuizGenerateDisabledReason(params: {
  canonicalGenerationComplete: boolean;
  blockOnlyCourse: boolean;
  hasStaticPlacement: boolean;
}): string | null {
  if (params.hasStaticPlacement) {
    return "This course uses static placement. Edit questions here instead of generating.";
  }
  if (!params.canonicalGenerationComplete) {
    return "Complete canonical lesson generation before using AI generation.";
  }
  if (params.blockOnlyCourse) {
    return "Block-format courses use static placement only.";
  }
  return null;
}

export interface CourseOrganization {
  organization_id: number;
  access_mode?: string;
  organizations: { id: number; name: string };
}

export interface CourseDetails {
  id: number;
  uuid?: string;
  title: string;
  description?: string | null;
  status: string;
  cover_image_url?: string | null;
  schema_version?: string | null;
  modules: Module[];
  levels: Level[];
  placement_quizzes: PlacementQuiz[];
  generation_prompt?: string | null;
  proactive_prompt?: string | null;
  quiz_prompt?: string | null;
  placement_quiz_prompt?: string | null;
  remedial_lesson_prompt?: string | null;
  course_organizations?: CourseOrganization[];
  is_public?: boolean;
  /** When true, students must pick a future goal in onboarding; when false, domain only. */
  require_user_future_goal?: boolean;
}

export interface CanonicalGenerationStatus {
  course_id: number;
  total_lessons: number;
  generated: number;
  in_progress: number;
  failed: number;
  pending: number;
  is_complete: boolean;
}

export interface CourseMetadataItem {
  id: number;
  label: string;
  type: "DOMAIN" | "FUTURE_GOAL";
}

export type PromptKey =
  | "generation_prompt"
  | "proactive_prompt"
  | "quiz_prompt"
  | "placement_quiz_prompt"
  | "remedial_lesson_prompt";

export const PROMPT_LABELS: Record<PromptKey, string> = {
  generation_prompt: "Lesson generation prompt",
  proactive_prompt: "Proactive prompt",
  quiz_prompt: "Quiz prompt",
  placement_quiz_prompt: "Placement quiz prompt",
  remedial_lesson_prompt: "Remedial lesson prompt",
};

export function getLessonStatus(lesson: Lesson): string {
  const raw = lesson.generated_lessons;
  const gls = Array.isArray(raw) ? raw : raw ? [raw] : [];
  if (gls.length === 0) {
    if (lesson._count?.content_chunks > 0) return "uploaded";
    return "not_started";
  }

  if (gls.every((gl) => gl.status === "APPROVED")) return "APPROVED";

  const statuses = gls.map((gl) => gl.status);
  if (statuses.includes("REJECTED")) return "REJECTED";
  if (statuses.includes("UNDER_REVIEW")) return "UNDER_REVIEW";
  if (statuses.includes("PENDING_REVIEW")) return "PENDING_REVIEW";
  if (statuses.includes("GENERATING")) return "GENERATING";
  return "PENDING_REVIEW";
}

export function getLessonQuizBadge(genLesson: GeneratedLesson | undefined): {
  label: string;
  color: string;
  title?: string;
} {
  if (!genLesson) {
    return { label: "Quiz —", color: "text-slate-500 border-slate-200" };
  }
  if (genLesson.quiz_generated) {
    return { label: "Quiz yes", color: "text-emerald-700 border-emerald-200" };
  }
  if (genLesson.quiz_generation_attempted && genLesson.quiz_generation_error) {
    return {
      label: "Quiz failed",
      color: "text-red-600 border-red-200",
      title: genLesson.quiz_generation_error,
    };
  }
  if (genLesson.quiz_generation_attempted) {
    return { label: "Quiz no", color: "text-amber-700 border-amber-200" };
  }
  return { label: "Quiz no", color: "text-slate-600 border-slate-200" };
}

export function getStatusParams(statusInput: string | undefined): { color: string; label: string } {
  const status = statusInput?.toLowerCase();
  switch (status) {
    case "approved":
      return { color: "text-emerald-500", label: "Approved" };
    case "pending_review":
      return { color: "text-amber-500", label: "Pending Review" };
    case "under_review":
      return { color: "text-amber-500", label: "Under Review" };
    case "rejected":
      return { color: "text-red-500", label: "Changes Requested" };
    case "generating":
      return { color: "text-blue-500", label: "Generating" };
    case "uploaded":
      return { color: "text-violet-500", label: "Uploaded" };
    default:
      return { color: "text-slate-400", label: "Not Generated" };
  }
}

export function getModuleStatusBadge(lessons: Lesson[]): { color: string; label: string; borderClass: string } {
  const lessonStatuses = lessons.map((lesson) => getLessonStatus(lesson));
  let moduleStatus = "APPROVED";

  if (lessonStatuses.includes("REJECTED")) moduleStatus = "REJECTED";
  else if (lessonStatuses.includes("UNDER_REVIEW")) moduleStatus = "UNDER_REVIEW";
  else if (lessonStatuses.includes("PENDING_REVIEW")) moduleStatus = "PENDING_REVIEW";
  else if (lessonStatuses.includes("not_started") || lessonStatuses.includes("uploaded")) moduleStatus = "PENDING_REVIEW";
  else if (lessonStatuses.every((status) => status === "APPROVED")) moduleStatus = "APPROVED";

  const { color, label } = getStatusParams(moduleStatus);
  const borderClass = color.replace("text-", "border-");
  return { color, label, borderClass };
}
